import React, { useState } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const CurrencySelector = ({ variant = 'header' }) => {
  const { currency, setCurrency } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);

  const currencies = [
    {
      code: 'GBP',
      name: 'British Pound',
      symbol: '£',
      flag: '🇬🇧',
      country: 'United Kingdom'
    },
    {
      code: 'USD',
      name: 'US Dollar',
      symbol: '$',
      flag: '🇺🇸',
      country: 'United States'
    },
    {
      code: 'EUR',
      name: 'Euro',
      symbol: '€',
      flag: '🇪🇺',
      country: 'European Union'
    }
  ];

  const currentCurrency = currencies.find(c => c.code === currency);

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    setIsOpen(false);
  };

  if (variant === 'mobile') {
    return (
      <div className="flex justify-center space-x-2">
        {currencies.map((curr) => (
          <button
            key={curr.code}
            onClick={() => handleCurrencyChange(curr.code)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
              currency === curr.code
                ? 'bg-[#FF5A3D] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>{curr.flag}</span>
            <span>{curr.symbol}</span>
            <span>{curr.code}</span>
          </button>
        ))}
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className="text-sm">
        <div className="flex items-center space-x-2 text-gray-400">
          <Globe size={16} />
          <span>Currency:</span>
        </div>
        <div className="flex space-x-2 mt-1">
          {currencies.map((curr) => (
            <button
              key={curr.code}
              onClick={() => handleCurrencyChange(curr.code)}
              className={`text-sm px-2 py-1 rounded transition-colors ${
                currency === curr.code
                  ? 'bg-[#FF5A3D] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {curr.flag} {curr.code}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex items-center space-x-2 text-gray-700 hover:text-[#FF5A3D] hover:bg-gray-50"
        >
          <span className="text-lg">{currentCurrency?.flag}</span>
          <span className="font-medium">{currentCurrency?.symbol}</span>
          <span className="text-sm">{currency}</span>
          <ChevronDown size={16} className={`transition-transform ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`} />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-64" align="end">
        {currencies.map((curr) => (
          <DropdownMenuItem
            key={curr.code}
            onClick={() => handleCurrencyChange(curr.code)}
            className={`flex items-center space-x-3 p-3 cursor-pointer ${
              currency === curr.code ? 'bg-[#FF5A3D]/10 text-[#FF5A3D]' : ''
            }`}
          >
            <span className="text-xl">{curr.flag}</span>
            <div className="flex-1">
              <div className="font-medium">{curr.name}</div>
              <div className="text-sm text-gray-500">{curr.country}</div>
            </div>
            <div className="font-bold text-lg">{curr.symbol}</div>
            {currency === curr.code && (
              <div className="w-2 h-2 bg-[#FF5A3D] rounded-full"></div>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CurrencySelector;